import * as React from "react";

import DeprecatedButton from "@v1/components/designSystem/button/Button";
import { DeprecatedEmptyState } from "@v1/components/designSystem/empty-state/EmptyState";
import { useAnalyticsQueryContext } from "@v1/features/analytics/contexts/AnalyticsQueryContext";
import { useOpenModal } from "@v2/store/modalStore";
import { docs } from "@v2/utils/docs";
import { ArrowUpRight, ChartLine, Rocket, Search } from "lucide-react";
import { exhaustiveSwitch } from "replo-utils/lib/misc";

export const AnalyticsEmptyState: React.FC<{
  type: "resetFilters" | "noTraffic" | "unlockAnalytics";
}> = ({ type = "noTraffic" }) => {
  return exhaustiveSwitch({ type })({
    resetFilters: <EmptyStateResetFilters />,
    noTraffic: <EmptyStateNoTraffic />,
    unlockAnalytics: <EmptyStateUnlockAnalytics />,
  });
};

const EmptyStateResetFilters: React.FC = () => {
  const { resetInternalAndUrlSyncedQueries } = useAnalyticsQueryContext();

  return (
    <DeprecatedEmptyState
      size="sm"
      variant="noRings"
      title="No matching results"
      description="Please adjust your filters and try again."
      layoutClassName="h-[300px]"
      icon={<Search size={18} />}
    >
      <DeprecatedButton
        size="sm"
        variant="primary"
        onClick={() => {
          resetInternalAndUrlSyncedQueries();
        }}
      >
        Reset filters
      </DeprecatedButton>
    </DeprecatedEmptyState>
  );
};

const EmptyStateNoTraffic: React.FC = () => {
  return (
    <DeprecatedEmptyState
      size="sm"
      variant="pinging"
      title="No traffic yet"
      layoutClassName="h-[300px]"
      description="We’ll populate this chart as soon as your pages start getting new traffic"
    >
      <DeprecatedButton
        size="sm"
        variant="primary"
        endEnhancer={<ArrowUpRight size={16} />}
        onClick={() => {
          window.open(docs.analytics.howToDriveTraffic, "_blank");
        }}
      >
        How to drive traffic
      </DeprecatedButton>
    </DeprecatedEmptyState>
  );
};

const EmptyStateUnlockAnalytics: React.FC = () => {
  const openModal = useOpenModal();

  const openBillingModal = () => {
    return openModal({
      type: "billingErrorModal",
      props: {
        billingPlanMessageKey: "billingPlan.analytics",
      },
    });
  };

  return (
    <DeprecatedEmptyState
      size="sm"
      variant="noRings"
      title="See How Your Store is Trending"
      layoutClassName="h-[300px]"
      description="See weekly trends & long-term growth patterns."
      icon={<ChartLine size={18} />}
    >
      <DeprecatedButton
        size="sm"
        variant="primary"
        startEnhancer={<Rocket size={14} />}
        onClick={openBillingModal}
      >
        Unlock
      </DeprecatedButton>
    </DeprecatedEmptyState>
  );
};
